import { Icon } from "./Icon";

/**
 * Numbered-node approval visuals, ported from the prototype.
 *
 * - `ApprovalPreview` is future-facing (a list of roles + who will hold each
 *   step) — used on the form runner's "after you submit" rail and the Review
 *   screen.
 * - `ApprovalTimeline` is the live state of an existing request (each step is
 *   done / current / rejected / todo) — used on the Confirmation screen and the
 *   My Requests detail slide-over.
 */

export interface PreviewStep {
  role: string;
  who: string;
}

export function ApprovalPreview({ steps }: { steps: PreviewStep[] }) {
  if (!steps.length) return null;
  return (
    <div className="apath">
      {steps.map((s, i) => (
        <div className="apath-step" key={i}>
          <div className="apath-rail">
            <div className="apath-node">{i + 1}</div>
            {i < steps.length - 1 && <div className="apath-line" />}
          </div>
          <div className="apath-body">
            <div className="apath-role">{s.role}</div>
            <div className="apath-name">{s.who}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

export type TimelineState = "done" | "current" | "rejected" | "todo";

export interface TimelineStep {
  role: string;
  name: string;
  state: TimelineState;
  at?: string | null;
  note?: string | null;
}

export function ApprovalTimeline({ path }: { path: TimelineStep[] }) {
  return (
    <div className="apath">
      {path.map((s, i) => {
        const nodeCls =
          s.state === "done"
            ? " done"
            : s.state === "current"
              ? " current"
              : s.state === "rejected"
                ? " rejected"
                : "";
        return (
          <div className="apath-step" key={i}>
            <div className="apath-rail">
              <div className={"apath-node" + nodeCls}>
                {s.state === "done" ? (
                  <Icon name="check" size={14} />
                ) : s.state === "rejected" ? (
                  <Icon name="x" size={14} />
                ) : (
                  i + 1
                )}
              </div>
              {i < path.length - 1 && (
                <div className={"apath-line" + (s.state === "done" ? " done" : "")} />
              )}
            </div>
            <div className="apath-body">
              <div className="apath-role">{s.role}</div>
              <div className="apath-name">{s.name}</div>
              <div className={"apath-meta" + (s.state === "current" ? " live" : "")}>
                {s.state === "current" ? "Holding now · " : ""}
                {s.at || (s.state === "todo" ? "Waiting for the previous step" : "")}
              </div>
              {s.note && (
                <div className="apath-meta" style={{ color: "#B42D32", marginTop: 4 }}>
                  &ldquo;{s.note}&rdquo;
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default ApprovalTimeline;
